'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Message, Channel, TypingUser, UserOnlineStatus } from '@/types/chat';
import { useAuth } from './auth-context';
import { chatWebSocket } from './chat-websocket-mock';
import {
    getMessages,
    sendMessage as dbSendMessage,
    getAllChannels,
    getUnreadCount,
    getTotalUnreadCount,
    markAsRead,
    incrementUnread,
} from '@/data/chat-database';

interface ChatContextType {
    // Messages
    messages: { [channelId: string]: Message[] };
    sendMessage: (content: string, channelId: string) => void;

    // Channels
    channels: Channel[];
    currentChannel: string | null;
    switchChannel: (channelId: string) => void;

    // Typing
    typingUsers: TypingUser[];
    setTyping: (channelId: string) => void;

    // Online status
    onlineUsers: string[];

    // Unread
    unreadCounts: { [channelId: string]: number };
    totalUnread: number;
    markChannelAsRead: (channelId: string) => void;

    // Panel state
    isChatOpen: boolean;
    toggleChat: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
    const { user, isAuthenticated } = useAuth();
    const [messages, setMessages] = useState<{ [channelId: string]: Message[] }>({});
    const [channels, setChannels] = useState<Channel[]>([]);
    const [currentChannel, setCurrentChannel] = useState<string | null>('global_all');
    const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [unreadCounts, setUnreadCounts] = useState<{ [channelId: string]: number }>({});
    const [totalUnread, setTotalUnread] = useState(0);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

    // Initialize channels
    useEffect(() => {
        const loadedChannels = getAllChannels();
        setChannels(loadedChannels);
    }, []);

    // Load messages for current channel
    useEffect(() => {
        if (!currentChannel) return;

        const loadedMessages = getMessages(currentChannel);
        setMessages(prev => ({
            ...prev,
            [currentChannel]: loadedMessages,
        }));
    }, [currentChannel]);

    // Load unread counts
    useEffect(() => {
        if (!user) return;

        const counts: { [channelId: string]: number } = {};
        channels.forEach(channel => {
            counts[channel.id] = getUnreadCount(user.id, channel.id);
        });
        setUnreadCounts(counts);
        setTotalUnread(getTotalUnreadCount(user.id));
    }, [channels, user]);

    // Start heartbeat when authenticated
    useEffect(() => {
        if (!user || !isAuthenticated) return;

        chatWebSocket.startHeartbeat(user.id);

        return () => {
            chatWebSocket.stopHeartbeat();
        };
    }, [user, isAuthenticated]);

    // Subscribe to WebSocket events
    useEffect(() => {
        const unsubscribe = chatWebSocket.subscribe((event) => {
            switch (event.type) {
                case 'new_message':
                    handleNewMessage(event.data);
                    break;
                case 'typing_start':
                    handleTypingStart(event.data);
                    break;
                case 'typing_stop':
                    handleTypingStop(event.data);
                    break;
                case 'user_online':
                    updateOnlineUsers();
                    break;
                default:
                    break;
            }
        });

        // Initial online users
        updateOnlineUsers();

        return unsubscribe;
    }, [user]);

    // Clean up typing indicators
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            setTypingUsers(prev =>
                prev.filter(tu => now - new Date(tu.timestamp).getTime() < 3000)
            );
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const handleNewMessage = useCallback((message: Message) => {
        setMessages(prev => ({
            ...prev,
            [message.channelId]: [...(prev[message.channelId] || []), message],
        }));

        // Increment unread if not current channel or chat closed
        if (user && (message.channelId !== currentChannel || !isChatOpen) && message.userId !== user.id) {
            incrementUnread(user.id, message.channelId);
            setUnreadCounts(prev => ({
                ...prev,
                [message.channelId]: (prev[message.channelId] || 0) + 1,
            }));
            setTotalUnread(prev => prev + 1);
        }
    }, [currentChannel, isChatOpen, user]);

    const handleTypingStart = useCallback((data: TypingUser) => {
        // Don't show own typing
        if (user && data.userId === user.id) return;

        setTypingUsers(prev => {
            const exists = prev.find(tu => tu.userId === data.userId && tu.channelId === data.channelId);
            if (exists) {
                return prev.map(tu =>
                    tu.userId === data.userId && tu.channelId === data.channelId
                        ? { ...tu, timestamp: data.timestamp }
                        : tu
                );
            }
            return [...prev, data];
        });
    }, [user]);

    const handleTypingStop = useCallback((data: { userId: string; channelId: string }) => {
        setTypingUsers(prev =>
            prev.filter(tu => !(tu.userId === data.userId && tu.channelId === data.channelId))
        );
    }, []);

    const updateOnlineUsers = useCallback(() => {
        const users = chatWebSocket.getOnlineUsers();
        setOnlineUsers(users);
    }, []);

    const sendMessage = useCallback((content: string, channelId: string) => {
        if (!user || !content.trim()) return;

        const message = dbSendMessage({
            channelId,
            channelType: 'global',
            userId: user.id,
            userName: user.fullName,
            userRole: user.role,
            userAvatar: user.avatar,
            type: 'user',
            content: content.trim(),
        });

        // Stop typing indicator
        if (typingTimeout) {
            clearTimeout(typingTimeout);
            setTypingTimeout(null);
        }
        chatWebSocket.stopTyping(user.id, channelId);
    }, [user, typingTimeout]);

    const switchChannel = useCallback((channelId: string) => {
        setCurrentChannel(channelId);

        // Mark as read
        if (user) {
            markAsRead(user.id, channelId);
            setUnreadCounts(prev => ({ ...prev, [channelId]: 0 }));
            setTotalUnread(getTotalUnreadCount(user.id));
        }
    }, [user]);

    const setTyping = useCallback((channelId: string) => {
        if (!user) return;

        chatWebSocket.sendTyping(user.id, user.fullName, channelId);

        // Auto-stop after 3 seconds
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        const timeout = setTimeout(() => {
            chatWebSocket.stopTyping(user.id, channelId);
        }, 3000);

        setTypingTimeout(timeout);
    }, [user, typingTimeout]);

    const markChannelAsRead = useCallback((channelId: string) => {
        if (!user) return;

        markAsRead(user.id, channelId);
        setUnreadCounts(prev => ({ ...prev, [channelId]: 0 }));
        setTotalUnread(getTotalUnreadCount(user.id));
    }, [user]);

    const toggleChat = useCallback(() => {
        setIsChatOpen(prev => !prev);

        // Mark current channel as read when opening
        if (!isChatOpen && currentChannel && user) {
            markChannelAsRead(currentChannel);
        }
    }, [isChatOpen, currentChannel, user, markChannelAsRead]);

    const value: ChatContextType = {
        messages,
        sendMessage,
        channels,
        currentChannel,
        switchChannel,
        typingUsers,
        setTyping,
        onlineUsers,
        unreadCounts,
        totalUnread,
        markChannelAsRead,
        isChatOpen,
        toggleChat,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}
